import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { io } from 'socket.io-client';
import api from '../../services/api';
import { getToken } from '../../utils/tokenStorage';
import {
  HomeIcon,
  UsersIcon,
  TruckIcon,
  MapIcon,
  MapPinIcon,
  CreditCardIcon,
  CogIcon,
  ArrowLeftOnRectangleIcon,
  Bars3Icon,
  XMarkIcon,
  BellIcon,
  BanknotesIcon,
  ClipboardDocumentListIcon,
  TicketIcon,
  GiftIcon,
  MegaphoneIcon,
  PhotoIcon,
  UserGroupIcon,
  StarIcon,
  ArrowPathIcon,
} from '@heroicons/react/24/outline';
import { can, canAny } from '../../hooks/usePermissions';

const navigation = [
  { name: 'Dashboard', href: '/', icon: HomeIcon },
  { name: 'Users', href: '/users', icon: UsersIcon },
  { name: 'Drivers', href: '/drivers', icon: TruckIcon },
  { name: 'Rides', href: '/rides', icon: MapIcon },
  { name: 'Standing Orders', href: '/standing-orders', icon: ArrowPathIcon, check: (a) => can(a, 'manage_rides') },
  { name: 'Payments', href: '/payments', icon: CreditCardIcon },
  { name: 'Payouts', href: '/payouts', icon: BanknotesIcon, check: (a) => canAny(a, ['manage_payments', 'manage_payouts']) },
  { name: 'Payroll', href: '/payroll', icon: BanknotesIcon, check: (a) => canAny(a, ['manage_payments', 'manage_payouts']) },
  { name: 'Ratings', href: '/ratings', icon: StarIcon, check: (a) => canAny(a, ['manage_drivers', 'manage_users']) },
  { name: 'Settings', href: '/settings', icon: CogIcon },
  {
    name: 'Service Areas',
    href: '/service-areas',
    icon: MapPinIcon,
    check: (a) => can(a, 'manage_settings') || can(a, 'manage_settings_zones'),
  },
  {
    name: 'Coupons',
    href: '/coupons',
    icon: TicketIcon,
    check: (a) => can(a, 'manage_settings') || can(a, 'manage_promotions'),
  },
  {
    name: 'Referrals',
    href: '/referrals',
    icon: GiftIcon,
    check: (a) => can(a, 'manage_settings') || can(a, 'manage_promotions'),
  },
  {
    name: 'Broadcasts',
    href: '/broadcasts',
    icon: MegaphoneIcon,
    check: (a) => can(a, 'manage_broadcasts') || can(a, 'manage_promotions'),
  },
  {
    name: 'Ads',
    href: '/ads',
    icon: PhotoIcon,
    check: (a) => can(a, 'manage_broadcasts') || can(a, 'manage_promotions'),
  },
  {
    name: 'Admin Accounts',
    href: '/admins',
    icon: UserGroupIcon,
    check: (a) => can(a, 'manage_admins'),
  },
  {
    name: 'Activity Log',
    href: '/audit-logs',
    icon: ClipboardDocumentListIcon,
    check: (a) => can(a, 'view_audit_logs'),
  },
];

const Layout = ({ children }) => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [notificationsOpen, setNotificationsOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [pendingDriversCount, setPendingDriversCount] = useState(0);
  const { admin, logout } = useAuth();
  const location = useLocation();
  const notificationPanelRef = useRef(null);

  const socketUrl = useMemo(() => {
    const apiBase = process.env.REACT_APP_API_URL || 'http://localhost:5050/api';
    return apiBase.replace(/\/api\/?$/, '');
  }, []);

  const unreadCount = useMemo(
    () => notifications.reduce((count, item) => (item.read ? count : count + 1), 0),
    [notifications]
  );

  const pushNotification = ({ title, message, href }) => {
    setNotifications((prev) => {
      const next = [
        {
          id: `${Date.now()}_${Math.random().toString(16).slice(2)}`,
          title,
          message,
          href,
          createdAt: new Date().toISOString(),
          read: false,
        },
        ...prev,
      ];
      return next.slice(0, 25);
    });
  };

  useEffect(() => {
    const token = getToken();
    if (!token) return () => {};

    const socket = io(socketUrl, {
      transports: ['websocket'],
      auth: { token: `Bearer ${token}` },
      autoConnect: false,
    });

    socket.connect();

    const onRideNewRequest = (payload = {}) => {
      pushNotification({
        title: 'New Ride Request',
        message: `${payload?.rideType || 'Ride'} • ${payload?.fare?.currency || ''} ${payload?.fare?.totalFare || ''}`.trim(),
        href: '/rides',
      });
    };

    const onRideAssigned = (payload = {}) => {
      pushNotification({
        title: 'Driver Assigned',
        message: `Ride #${String(payload?._id || '').slice(-6)} assigned`,
        href: payload?._id ? `/rides/${payload._id}` : '/rides',
      });
    };

    const onRideStatusChanged = (payload = {}) => {
      pushNotification({
        title: 'Ride Status Updated',
        message: `Ride #${String(payload?._id || '').slice(-6)} is now ${payload?.status || 'updated'}`,
        href: payload?._id ? `/rides/${payload._id}` : '/rides',
      });
    };

    const onRideCancelled = (payload = {}) => {
      pushNotification({
        title: 'Ride Cancelled',
        message: `Ride #${String(payload?.rideId || '').slice(-6)} cancelled by ${payload?.cancelledBy || 'unknown'}`,
        href: payload?.rideId ? `/rides/${payload.rideId}` : '/rides',
      });
    };

    const onSupportTicketUpdated = (payload = {}) => {
      pushNotification({
        title: 'Support Ticket Updated',
        message: payload?.subject || 'A support ticket has new activity',
        href: '/settings/support',
      });
    };

    socket.on('ride:newRequest', onRideNewRequest);
    socket.on('ride:driverAssigned', onRideAssigned);
    socket.on('ride:statusChanged', onRideStatusChanged);
    socket.on('ride:cancelled', onRideCancelled);
    socket.on('support:ticket_updated', onSupportTicketUpdated);

    return () => {
      socket.off('ride:newRequest', onRideNewRequest);
      socket.off('ride:driverAssigned', onRideAssigned);
      socket.off('ride:statusChanged', onRideStatusChanged);
      socket.off('ride:cancelled', onRideCancelled);
      socket.off('support:ticket_updated', onSupportTicketUpdated);
      socket.disconnect();
    };
  }, [socketUrl]);

  useEffect(() => {
    let mounted = true;

    const fetchStats = async () => {
      try {
        const response = await api.get('/admin/dashboard/stats');
        const nextPendingCount = Number(response?.data?.data?.pendingDrivers || 0);

        if (!mounted) return;
        if (pendingDriversCount > 0 && nextPendingCount > pendingDriversCount) {
          pushNotification({
            title: 'New Pending Driver',
            message: `${nextPendingCount - pendingDriversCount} new driver application(s) pending review`,
            href: '/drivers?status=pending',
          });
        }
        setPendingDriversCount(nextPendingCount);
      } catch (_error) {}
    };

    fetchStats();
    const interval = setInterval(fetchStats, 30000);

    return () => {
      mounted = false;
      clearInterval(interval);
    };
  }, [pendingDriversCount]);

  useEffect(() => {
    const onClickOutside = (event) => {
      if (!notificationPanelRef.current) return;
      if (!notificationPanelRef.current.contains(event.target)) {
        setNotificationsOpen(false);
      }
    };

    document.addEventListener('mousedown', onClickOutside);
    return () => document.removeEventListener('mousedown', onClickOutside);
  }, []);

  const markAllRead = () => {
    setNotifications((prev) => prev.map((item) => ({ ...item, read: true })));
  };

  const formatNotificationTime = (iso) => {
    const date = new Date(iso);
    if (Number.isNaN(date.getTime())) return '';
    const diffSec = Math.floor((Date.now() - date.getTime()) / 1000);
    if (diffSec < 60) return 'just now';
    const diffMin = Math.floor(diffSec / 60);
    if (diffMin < 60) return `${diffMin}m ago`;
    const diffHour = Math.floor(diffMin / 60);
    if (diffHour < 24) return `${diffHour}h ago`;
    return date.toLocaleDateString();
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Mobile sidebar backdrop */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-gray-600 bg-opacity-50 z-20 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside
        className={`fixed inset-y-0 left-0 z-30 w-64 bg-white shadow-lg transform transition-transform duration-300 lg:translate-x-0 ${
          sidebarOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
      >
        <div className="flex items-center justify-between h-16 px-6 border-b">
          <Link to="/" className="flex items-center space-x-2">
            <div className="w-8 h-8 rounded-lg flex items-center justify-center overflow-hidden">
              <img src="/logo.png" alt="She &amp; She" className="w-full h-full object-contain" />
            </div>
            <span className="text-xl font-bold text-gray-800">She & She</span>
          </Link>
          <button
            className="lg:hidden"
            onClick={() => setSidebarOpen(false)}
          >
            <XMarkIcon className="w-6 h-6 text-gray-500" />
          </button>
        </div>

        <nav className="p-4 space-y-1">
          {navigation.filter((item) => !item.check || item.check(admin)).map((item) => {
            const isActive = location.pathname === item.href || 
              (item.href !== '/' && location.pathname.startsWith(item.href));
            return (
              <Link
                key={item.name}
                to={item.href}
                className={`sidebar-link ${isActive ? 'active' : ''}`}
                onClick={() => setSidebarOpen(false)}
              >
                <item.icon className="w-5 h-5 mr-3" />
                {item.name}
              </Link>
            );
          })}
        </nav>

        <div className="absolute bottom-0 left-0 right-0 p-4 border-t">
          <button
            onClick={logout}
            className="sidebar-link w-full text-red-600 hover:bg-red-50 hover:text-red-700"
          >
            <ArrowLeftOnRectangleIcon className="w-5 h-5 mr-3" />
            Logout
          </button>
        </div>
      </aside>

      {/* Main content */}
      <div className="lg:ml-64">
        {/* Top navbar */}
        <header className="sticky top-0 z-10 bg-white shadow-sm">
          <div className="flex items-center justify-between h-16 px-6">
            <button
              className="lg:hidden"
              onClick={() => setSidebarOpen(true)}
            >
              <Bars3Icon className="w-6 h-6 text-gray-500" />
            </button>

            <div className="flex-1 lg:flex-none" />

            <div className="flex items-center space-x-4">
              <div className="relative" ref={notificationPanelRef}>
                <button
                  className="relative p-2 text-gray-500 hover:text-gray-700"
                  onClick={() => setNotificationsOpen((prev) => !prev)}
                >
                  <BellIcon className="w-6 h-6" />
                  {unreadCount > 0 && (
                    <span className="absolute -top-0.5 -right-0.5 min-w-[16px] h-4 px-1 bg-red-500 rounded-full text-[10px] leading-4 text-white text-center">
                      {unreadCount > 9 ? '9+' : unreadCount}
                    </span>
                  )}
                </button>

                {notificationsOpen && (
                  <div className="absolute right-0 mt-2 w-80 bg-white rounded-lg shadow-lg border border-gray-200 z-30">
                    <div className="flex items-center justify-between px-4 py-3 border-b">
                      <p className="text-sm font-semibold text-gray-800">Notifications</p>
                      {notifications.length > 0 && (
                        <button className="text-xs text-indigo-600 hover:text-indigo-800" onClick={markAllRead}>
                          Mark all read
                        </button>
                      )}
                    </div>

                    <div className="max-h-96 overflow-auto">
                      {notifications.length === 0 ? (
                        <p className="px-4 py-6 text-sm text-gray-500 text-center">No notifications yet</p>
                      ) : (
                        notifications.map((item) => (
                          <Link
                            key={item.id}
                            to={item.href || '#'}
                            onClick={() => {
                              setNotifications((prev) =>
                                prev.map((entry) =>
                                  entry.id === item.id ? { ...entry, read: true } : entry
                                )
                              );
                              setNotificationsOpen(false);
                            }}
                            className={`block px-4 py-3 border-b last:border-b-0 hover:bg-gray-50 ${
                              item.read ? 'bg-white' : 'bg-indigo-50/50'
                            }`}
                          >
                            <p className="text-sm font-medium text-gray-800">{item.title}</p>
                            <p className="text-xs text-gray-600 mt-1">{item.message}</p>
                            <p className="text-[11px] text-gray-400 mt-1">{formatNotificationTime(item.createdAt)}</p>
                          </Link>
                        ))
                      )}
                    </div>
                  </div>
                )}
              </div>
              
              <div className="flex items-center space-x-3">
                <div className="w-8 h-8 bg-indigo-100 rounded-full flex items-center justify-center">
                  <span className="text-indigo-600 font-medium text-sm">
                    {admin?.firstName?.[0]}{admin?.lastName?.[0]}
                  </span>
                </div>
                <div className="hidden sm:block">
                  <p className="text-sm font-medium text-gray-700">
                    {admin?.firstName} {admin?.lastName}
                  </p>
                  <p className="text-xs text-gray-500 capitalize">{admin?.role}</p>
                </div>
              </div>
            </div>
          </div>
        </header>

        {/* Page content */}
        <main className="p-6">
          {children}
        </main>
      </div>
    </div>
  );
};

export default Layout;
